import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const total = Math.floor(seconds);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const sec = s.toString().padStart(2, "0");
  if (h > 0) {
    const min = m.toString().padStart(2, "0");
    return `${h}:${min}:${sec}`;
  }
  return `${m}:${sec}`;
}

export type ThemedAudioPlayerProps = {
  src: string;
  ariaLabel: string;
  className?: string;
};

const seekRangeClassName = cn(
  "themed-audio-seek accent-primary h-4 w-full min-w-0 cursor-pointer appearance-none rounded-full bg-transparent py-2",
  /* WebKit track */
  "[&::-webkit-slider-runnable-track]:h-3.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-white/20 [&::-webkit-slider-runnable-track]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.25)] [&::-webkit-slider-runnable-track]:ring-1 [&::-webkit-slider-runnable-track]:ring-inset [&::-webkit-slider-runnable-track]:ring-white/15",
  /* WebKit thumb */
  "[&::-webkit-slider-thumb]:-mt-0.5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_2px_8px_-1px_rgba(0,0,0,0.45)] [&::-webkit-slider-thumb]:ring-2 [&::-webkit-slider-thumb]:ring-primary/40 active:[&::-webkit-slider-thumb]:cursor-grabbing",
  /* Firefox filled segment */
  "[&::-moz-range-progress]:h-3.5 [&::-moz-range-progress]:rounded-full [&::-moz-range-progress]:bg-gradient-to-r [&::-moz-range-progress]:from-primary [&::-moz-range-progress]:to-gold",
  /* Firefox track (unfilled portion) */
  "[&::-moz-range-track]:h-3.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-white/20 [&::-moz-range-track]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.25)] [&::-moz-range-track]:ring-1 [&::-moz-range-track]:ring-inset [&::-moz-range-track]:ring-white/15",
  /* Firefox thumb */
  "[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-[0_2px_8px_-1px_rgba(0,0,0,0.45)]",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
);

export function ThemedAudioPlayer({
  src,
  ariaLabel,
  className,
}: ThemedAudioPlayerProps) {
  const { t } = useLanguage();
  const audioRef = useRef<HTMLAudioElement>(null);
  const rangePointerActiveRef = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  const safeDuration =
    Number.isFinite(duration) && duration > 0 && duration !== Number.POSITIVE_INFINITY
      ? duration
      : 0;

  const seekEnabled = safeDuration > 0;
  const rangeMax = seekEnabled ? safeDuration : 1;

  const syncDurationFromAudio = useCallback(() => {
    const a = audioRef.current;
    if (!a) {
      return;
    }
    const d = a.duration;
    if (Number.isFinite(d) && d > 0 && d !== Number.POSITIVE_INFINITY) {
      setDuration(d);
    } else {
      setDuration(0);
    }
  }, []);

  const seekToSeconds = useCallback((selectedTime: number) => {
    const a = audioRef.current;
    if (!a) {
      return;
    }
    const d = a.duration;
    if (!Number.isFinite(d) || d <= 0 || d === Number.POSITIVE_INFINITY) {
      return;
    }
    const tTime = Math.max(0, Math.min(d, selectedTime));
    try {
      a.currentTime = tTime;
    } catch {
      return;
    }
    setCurrentTime(tTime);
  }, []);

  const endRangeGesture = useCallback(() => {
    rangePointerActiveRef.current = false;
    const a = audioRef.current;
    if (a && Number.isFinite(a.currentTime)) {
      setCurrentTime(a.currentTime);
    }
  }, []);

  useEffect(() => {
    const end = () => {
      endRangeGesture();
    };
    window.addEventListener("pointerup", end, true);
    window.addEventListener("pointercancel", end, true);
    window.addEventListener("touchend", end, true);
    return () => {
      window.removeEventListener("pointerup", end, true);
      window.removeEventListener("pointercancel", end, true);
      window.removeEventListener("touchend", end, true);
    };
  }, [endRangeGesture]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) {
      return;
    }
    setCurrentTime(0);
    setPlaying(false);
    setDuration(0);
    setMuted(false);
    a.muted = false;
    try {
      a.load();
    } catch {
      /* noop */
    }
  }, [src]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) {
      return;
    }

    const onTimeUpdate = () => {
      if (rangePointerActiveRef.current) {
        return;
      }
      if (Number.isFinite(a.currentTime)) {
        setCurrentTime(a.currentTime);
      }
    };

    const onLoadedMetadata = () => {
      syncDurationFromAudio();
    };

    const onDurationChange = () => {
      syncDurationFromAudio();
    };

    const onEnded = () => setPlaying(false);
    const syncPlay = () => setPlaying(!a.paused);

    const onSeeked = () => {
      if (rangePointerActiveRef.current) {
        return;
      }
      if (Number.isFinite(a.currentTime)) {
        setCurrentTime(a.currentTime);
      }
    };

    a.addEventListener("timeupdate", onTimeUpdate);
    a.addEventListener("loadedmetadata", onLoadedMetadata);
    a.addEventListener("durationchange", onDurationChange);
    a.addEventListener("ended", onEnded);
    a.addEventListener("play", syncPlay);
    a.addEventListener("pause", syncPlay);
    a.addEventListener("seeked", onSeeked);

    return () => {
      a.removeEventListener("timeupdate", onTimeUpdate);
      a.removeEventListener("loadedmetadata", onLoadedMetadata);
      a.removeEventListener("durationchange", onDurationChange);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("play", syncPlay);
      a.removeEventListener("pause", syncPlay);
      a.removeEventListener("seeked", onSeeked);
    };
  }, [src, syncDurationFromAudio]);

  const togglePlay = useCallback(async () => {
    const a = audioRef.current;
    if (!a) {
      return;
    }
    if (a.paused) {
      try {
        await a.play();
      } catch {
        setPlaying(false);
      }
    } else {
      a.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const a = audioRef.current;
    if (!a) {
      return;
    }
    a.muted = !a.muted;
    setMuted(a.muted);
  }, []);

  const handleRangeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      if (!Number.isFinite(v)) {
        return;
      }
      seekToSeconds(v);
    },
    [seekToSeconds]
  );

  const sliderValue = seekEnabled
    ? Math.min(Math.max(currentTime, 0), safeDuration)
    : 0;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn("w-full min-w-0", className)}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        aria-hidden
        className="sr-only"
      />

      <div className="flex items-start gap-3 sm:gap-4">
        <button
          type="button"
          onClick={() => void togglePlay()}
          className={cn(
            "flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all duration-200 sm:size-12",
            "border border-white/30 bg-white/15 text-white shadow-[0_8px_24px_-8px_rgba(0,0,0,0.35)] backdrop-blur-sm",
            "hover:border-primary/60 hover:bg-white/25 hover:shadow-[0_10px_28px_-8px_hsl(220,60%,40%,0.45)]",
            "focus-visible:ring-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          )}
          aria-label={
            playing ? t("termDetail.audioPause") : t("termDetail.audioPlay")
          }
        >
          {playing ? (
            <Pause className="h-5 w-5 fill-current sm:h-6 sm:w-6" />
          ) : (
            <Play className="ml-0.5 h-5 w-5 fill-current sm:h-6 sm:w-6" />
          )}
        </button>

        <div className="flex min-h-[4.75rem] min-w-0 flex-1 flex-col justify-center gap-2">
          <div
            className="w-full min-w-0 px-px"
            onWheel={e => {
              const a = audioRef.current;
              if (!a || !seekEnabled || a.seeking) {
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              const deltaSec = e.deltaY > 0 ? -8 : 8;
              const d = a.duration;
              if (!Number.isFinite(d) || d <= 0) {
                return;
              }
              seekToSeconds(a.currentTime + deltaSec);
            }}
          >
            <input
              type="range"
              aria-label={ariaLabel}
              aria-valuemin={0}
              aria-valuemax={Math.ceil(rangeMax)}
              aria-valuenow={Math.round(sliderValue)}
              aria-valuetext={`${formatTime(sliderValue)} / ${formatTime(safeDuration)}`}
              className={seekRangeClassName}
              min={0}
              max={rangeMax}
              step={0.01}
              value={sliderValue}
              disabled={!seekEnabled}
              onPointerDown={() => {
                rangePointerActiveRef.current = true;
              }}
              onChange={handleRangeChange}
              onInput={handleRangeChange}
              onPointerUp={endRangeGesture}
              onPointerCancel={endRangeGesture}
            />
          </div>

          <div className="text-white/65 flex items-center justify-between gap-4 font-semibold tabular-nums tracking-wide">
            <span className="text-xs sm:text-sm">
              {formatTime(currentTime)}
            </span>
            <span className="text-xs sm:text-sm">
              {formatTime(safeDuration)}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={toggleMute}
          className={cn(
            "flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors sm:size-11",
            "text-white/75 hover:bg-white/15 hover:text-white",
            "focus-visible:ring-primary/60 focus-visible:outline-none focus-visible:ring-2"
          )}
          aria-label={
            muted ? t("termDetail.audioUnmute") : t("termDetail.audioMute")
          }
        >
          {muted ? (
            <VolumeX className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
          ) : (
            <Volume2 className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
          )}
        </button>
      </div>
    </div>
  );
}
